import { Building2, Users, Key, Shield } from "lucide-react";
import { StatCard } from "@/components/dashboard/StatCard";
import { SystemStatusCard } from "@/components/dashboard/SystemStatusCard";
import { MusicStatsCard } from "@/components/dashboard/MusicStatsCard";
import { DeviceStatusCard } from "@/components/dashboard/DeviceStatusCard";
import { FinancialMetricsCard } from "@/components/dashboard/FinancialMetricsCard";
import { useQuery } from "@tanstack/react-query";
import axios from '@/lib/axios';

// Mock veriler
const mockSystemMetrics = {
  cpu: 65,
  memory: 45,
  disk: 80,
  uptime: "5 gün 12 saat"
};

const mockMusicStats = {
  totalSongs: 1250,
  activePlaylists: 45,
  storageUsed: "250GB",
  topSongs: [
    { id: "1", title: "Shape of You", artist: "Ed Sheeran", plays: 1500 },
    { id: "2", title: "Blinding Lights", artist: "The Weeknd", plays: 1200 },
    { id: "3", title: "Dance Monkey", artist: "Tones and I", plays: 1100 },
    { id: "4", title: "Someone You Loved", artist: "Lewis Capaldi", plays: 900 },
    { id: "5", title: "Stay", artist: "Kid Laroi & Justin Bieber", plays: 850 }
  ]
};

const mockDevices = [
  {
    id: "1",
    name: "Jukebox #001",
    status: "online" as const,
    location: "İstanbul/Kadıköy",
    lastSeen: "Şimdi"
  },
  {
    id: "2",
    name: "Jukebox #002",
    status: "online" as const,
    location: "İstanbul/Beşiktaş",
    lastSeen: "5 dk önce"
  },
  {
    id: "3",
    name: "Jukebox #003",
    status: "offline" as const,
    location: "Ankara/Çankaya",
    lastSeen: "2 saat önce"
  }
];

const mockFinancialData = {
  monthlyRevenue: 45000,
  customerGrowth: 15,
  renewalRate: 92,
  activeSubscriptions: 150,
  revenueHistory: [
    { month: "Ocak", revenue: 35000, subscriptions: 120 },
    { month: "Şubat", revenue: 38000, subscriptions: 130 },
    { month: "Mart", revenue: 42000, subscriptions: 140 },
    { month: "Nisan", revenue: 45000, subscriptions: 150 },
    { month: "Mayıs", revenue: 48000, subscriptions: 160 }
  ]
};

const DashboardPage = () => {
  const { data: stats = { companies: 0, licenses: 0, users: 0 } } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: async () => {
      const response = await axios.get('/stats/dashboard');
      return response.data;
    }
  });

  return (
    <div className="h-full flex-1">
      <div className="flex-1 space-y-6 p-8 pt-6">
        {/* Hero Section */}
        <div className="rounded-2xl overflow-hidden shadow-lg">
          <div
            className="flex"
            style={{
              background: 'linear-gradient(to right, #6b21a8, #581c87)',
            }}
          >
            <div className="p-8 flex justify-between items-start relative w-full">
              <div className="space-y-3 flex-1">
                <div className="text-white/80 text-sm font-medium">
                  Super Admin Paneli
                </div>
                <h1 className="text-3xl font-bold text-white">
                  Firma ve Sistem Yönetimi
                </h1>
                <p className="text-white/90 text-sm max-w-xl leading-relaxed">
                  Cloud Jukebox sisteminin tüm firmalarını, lisanslarını ve kullanıcılarını
                  yönetin. Sistem performansını izleyin, cihaz durumlarını kontrol edin ve
                  müzik kütüphanesini organize edin.
                </p>
              </div>
              <div
                className="w-40 h-40 rounded-lg overflow-hidden relative z-10 flex items-center justify-center ml-8"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                }}
              >
                <Shield className="w-20 h-20 text-white" strokeWidth={1.5} />
              </div>
            </div>
          </div>
        </div>

        {/* Ana Metrikler */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <StatCard
            title="Toplam Firma"
            value={stats.companies}
            icon={Building2}
            trend={{ value: 12, isPositive: true }}
            description="Aktif firma sayısı"
            variant="blue"
          />
          <StatCard
            title="Aktif Lisanslar"
            value={stats.licenses}
            icon={Key}
            trend={{ value: 8, isPositive: true }}
            description="Kullanımda olan lisanslar"
            variant="purple"
          />
          <StatCard
            title="Toplam Kullanıcı"
            value={stats.users}
            icon={Users}
            trend={{ value: 5, isPositive: true }}
            description="Kayıtlı kullanıcı sayısı"
            variant="green"
          />
        </div>

        {/* Sistem ve Müzik İstatistikleri */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <SystemStatusCard metrics={mockSystemMetrics} />
          <MusicStatsCard stats={mockMusicStats} />
        </div>

        {/* Alt Bölüm - Cihazlar ve Finansal */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-1">
            <DeviceStatusCard devices={mockDevices} />
          </div>
          <div className="lg:col-span-2">
            <FinancialMetricsCard data={mockFinancialData} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;